import React, { useState, useEffect, useCallback } from 'react';
import { GarageCheckin, GarageAgent } from '../types';
import { supabase } from '../supabaseClient';
import { Car, CheckCircle2, Clock, Loader2, LogIn, RefreshCw, ArrowUpDown, LayoutGrid, List, Filter, Shield, AlertTriangle, X, Repeat, MapPin } from 'lucide-react';
import { INITIAL_FLEET } from './FleetView';

const normalizePlate = (plate: string) => {
    return (plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
};

const getCarAddress = (plate: string, loadedCars: any[]) => {
    const normalized = normalizePlate(plate);
    if (!normalized) return '';
    
    // 1. Search in database loaded cars
    const dbMatch = loadedCars.find(c => normalizePlate(c.placa) === normalized);
    if (dbMatch) return dbMatch.endereco;

    // 2. Search in INITIAL_FLEET fallback
    const fallbackMatch = INITIAL_FLEET.find(c => normalizePlate(c.placa) === normalized);
    if (fallbackMatch) return fallbackMatch.endereco;

    return '';
};

// --- Audit Color Calculator (mirrors edge function) ---
function calculateAuditColor(checkin: GarageCheckin): string {
    if (checkin.is_bilingual_start) return 'BLUE';
    if (!checkin.car_arrived_at || !checkin.login_telegram_at) return checkin.audit_color || 'GRAY';
    const carTime = new Date(checkin.car_arrived_at).getTime();
    const [h, m] = (checkin.scheduled_start || '00:00').split(':').map(Number);
    const sd = new Date(checkin.shift_date + 'T00:00:00');
    sd.setHours(h, m, 0, 0);
    const ref = Math.max(carTime, sd.getTime());
    const login = new Date(checkin.login_telegram_at).getTime();
    const diff = (login - ref) / 60000;
    if (checkin.token_delay && checkin.token_proof_url) return diff <= 8 ? 'GREEN' : 'YELLOW';
    if (diff <= 5) return 'GREEN';
    if (diff <= 8) return 'YELLOW';
    return 'RED';
}

const colorStyles: Record<string, { bg: string; border: string; text: string; dot: string }> = {
    GRAY: { bg: 'bg-slate-800/60', border: 'border-slate-700', text: 'text-slate-400', dot: 'bg-slate-500' },
    GREEN: { bg: 'bg-emerald-950/40', border: 'border-emerald-500/50', text: 'text-emerald-400', dot: 'bg-emerald-500' },
    YELLOW: { bg: 'bg-amber-950/40', border: 'border-amber-500/50', text: 'text-amber-400', dot: 'bg-amber-500' },
    RED: { bg: 'bg-red-950/40', border: 'border-red-500/50', text: 'text-red-400', dot: 'bg-red-500' },
    BLUE: { bg: 'bg-blue-950/40', border: 'border-blue-500/50', text: 'text-blue-400', dot: 'bg-blue-500' },
};

const statusEmoji: Record<string, string> = { WAITING: '⏳', CAR_READY: '🚗', DRIVER_OK: '📲', COMPLETED: '✅' };

// --- Login Screen ---
const AgentLogin: React.FC<{ agents: GarageAgent[]; onLogin: (agent: GarageAgent) => void }> = ({ agents, onLogin }) => {
    const [selectedId, setSelectedId] = useState('');
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async () => {
        if (!selectedId || !pin) { setError('Selecione seu nome e digite o PIN.'); return; }
        setLoading(true); setError('');
        const agent = agents.find(a => a.id === selectedId);
        if (!agent || agent.pin !== pin) { setError('PIN inválido.'); setLoading(false); return; }
        onLogin(agent);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 w-full max-w-sm shadow-2xl">
                <div className="flex flex-col items-center gap-3 mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <Shield className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-xl font-bold text-white">Garagem Trianon MASP</h1>
                    <p className="text-slate-500 text-sm">Login do Agente</p>
                </div>
                <div className="space-y-4">
                    <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:ring-2 focus:ring-emerald-500/50">
                        <option value="">Selecione seu nome</option>
                        {agents.filter(a => a.is_active).map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                    </select>
                    <input type="password" inputMode="numeric" maxLength={6} placeholder="PIN" value={pin}
                        onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                        onKeyDown={e => e.key === 'Enter' && handleLogin()}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-center text-2xl tracking-[0.5em] outline-none focus:ring-2 focus:ring-emerald-500/50" />
                    {error && <p className="text-red-400 text-xs text-center">{error}</p>}
                    <button onClick={handleLogin} disabled={loading}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50">
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5" /> Entrar</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Checkin Card ---
const CheckinCard: React.FC<{
    checkin: GarageCheckin;
    agent: GarageAgent;
    onAction: (action: string, checkinId: string, data?: any) => Promise<void>;
    loading: string | null;
    fleetCars: any[];
}> = ({ checkin, agent, onAction, loading, fleetCars }) => {
    const color = calculateAuditColor(checkin);
    const s = colorStyles[color] || colorStyles.GRAY;
    const isLoading = loading === checkin.id;
    const [swapPlate, setSwapPlate] = useState('');
    const [showSwap, setShowSwap] = useState(false);

    const fmt = (iso?: string) => iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '--:--';

    return (
        <div className={`${s.bg} border ${s.border} rounded-xl p-3 transition-all hover:shadow-lg relative group`}>
            {/* Color dot indicator */}
            <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${s.dot} ${color === 'RED' ? 'animate-pulse' : ''}`} />

            {/* Driver info */}
            <div className="flex items-center gap-2.5 mb-2">
                <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center text-white font-bold text-xs shrink-0">
                    {(checkin.driver_nickname || 'M').substring(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                    <h4 className="text-white font-bold text-sm truncate">{checkin.driver_nickname || 'Motorista'}</h4>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <span className="font-mono font-bold text-slate-300">{checkin.scheduled_start}</span>
                        <span>•</span>
                        <span className="font-mono">{checkin.vehicle_plate}</span>
                        {checkin.car_swapped && <span className="text-amber-400 font-bold">TROCADO</span>}
                    </div>
                    {getCarAddress(checkin.vehicle_plate, fleetCars) && (
                        <div className="text-[10px] text-slate-500 font-medium flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            <span className="truncate">{getCarAddress(checkin.vehicle_plate, fleetCars)}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Timestamps grid */}
            <div className="grid grid-cols-2 gap-1.5 text-[10px] mb-2.5">
                <div className="bg-slate-900/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                    <Car className="w-3 h-3 text-slate-500" />
                    <span className="text-slate-500">Carro:</span>
                    <span className={`font-mono font-bold ${checkin.car_arrived_at ? 'text-emerald-400' : 'text-slate-600'}`}>
                        {fmt(checkin.car_arrived_at)}
                    </span>
                </div>
                <div className="bg-slate-900/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-slate-500" />
                    <span className="text-slate-500">Motorista:</span>
                    <span className={`font-mono font-bold ${checkin.driver_confirmed_at ? 'text-blue-400' : 'text-slate-600'}`}>
                        {fmt(checkin.driver_confirmed_at)}
                    </span>
                </div>
                <div className="bg-slate-900/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                    <span className="text-[8px]">📲</span>
                    <span className="text-slate-500">Telegram:</span>
                    <span className={`font-mono font-bold ${checkin.login_telegram_at ? s.text : 'text-slate-600'}`}>
                        {fmt(checkin.login_telegram_at)}
                    </span>
                </div>
                <div className="bg-slate-900/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                    <span className="text-[8px]">📱</span>
                    <span className="text-slate-500">App:</span>
                    <span className={`font-mono font-bold ${checkin.login_app_at ? 'text-cyan-400' : 'text-slate-600'}`}>
                        {fmt(checkin.login_app_at)}
                    </span>
                </div>
            </div>

            {/* Exception badges */}
            {(checkin.token_delay || checkin.car_swapped || checkin.is_bilingual_start) && (
                <div className="flex gap-1.5 mb-2.5 flex-wrap">
                    {checkin.is_bilingual_start && (
                        <span className="text-[9px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full font-bold">
                            🌎 Fluxo Bilíngue
                        </span>
                    )}
                    {checkin.token_delay && (
                        <span className="text-[9px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-bold">
                            ⏳ Token Delay {checkin.token_proof_url ? '✓' : ''}
                        </span>
                    )}
                    {checkin.car_swapped && (
                        <span className="text-[9px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full font-bold">
                            🔄 Troca → {checkin.swap_new_plate}
                        </span>
                    )}
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 mt-1">
                {checkin.status === 'WAITING' && (
                    <button onClick={() => onAction('car_arrived', checkin.id)} disabled={isLoading}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all disabled:opacity-50">
                        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Car className="w-3.5 h-3.5" /> Carro Chegou</>}
                    </button>
                )}

                {!checkin.agent_validated && checkin.status !== 'WAITING' && (
                    <>
                        <button onClick={() => onAction('validate', checkin.id)} disabled={isLoading}
                            className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all disabled:opacity-50">
                            {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5" /> Validar</>}
                        </button>
                        <button onClick={() => onAction('deny', checkin.id)} disabled={isLoading}
                            className="flex-1 bg-red-950/60 hover:bg-red-900/80 text-red-200 border border-red-500/30 text-[11px] font-bold py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all disabled:opacity-50" title="Negar Chegada/Validação">
                            {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><X className="w-3.5 h-3.5" /> Negar</>}
                        </button>
                        {!checkin.is_bilingual_start && (
                            <button onClick={() => onAction('set_bilingual', checkin.id)} disabled={isLoading}
                                className="bg-blue-900/50 hover:bg-blue-800/50 text-blue-400 text-[11px] font-bold py-2 px-3 rounded-lg transition-all disabled:opacity-50" title="Marcar como Bilíngue">
                                🌎
                            </button>
                        )}
                        <button onClick={() => setShowSwap(!showSwap)}
                            className="bg-slate-700 hover:bg-slate-600 text-white text-[11px] py-2 px-3 rounded-lg transition-all" title="Trocar Carro">
                            <Repeat className="w-3.5 h-3.5" />
                        </button>
                    </>
                )}

                {checkin.agent_validated && (
                    <div className="flex-1 bg-emerald-900/30 border border-emerald-500/30 text-emerald-400 text-[11px] font-bold py-2 rounded-lg flex items-center justify-center gap-1.5">
                        <Shield className="w-3.5 h-3.5" /> Validado
                    </div>
                )}
            </div>

            {/* Swap car inline */}
            {showSwap && (
                <div className="mt-2 flex gap-2">
                    <input value={swapPlate} onChange={e => setSwapPlate(e.target.value.toUpperCase())} placeholder="Nova placa"
                        className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-xs outline-none" />
                    <button onClick={async () => { await onAction('swap_car', checkin.id, { new_plate: swapPlate }); setShowSwap(false); setSwapPlate(''); }}
                        disabled={!swapPlate || isLoading}
                        className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-50">
                        Trocar
                    </button>
                </div>
            )}
        </div>
    );
};

// =================== MAIN COMPONENT ===================
export const GarageAgentView: React.FC = () => {
    const [agents, setAgents] = useState<GarageAgent[]>([]);
    const [currentAgent, setCurrentAgent] = useState<GarageAgent | null>(null);
    const [checkins, setCheckins] = useState<GarageCheckin[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'LIST' | 'KANBAN'>('LIST');
    const [filter, setFilter] = useState<'ALL' | 'WAITING' | 'CAR_READY' | 'DRIVER_OK' | 'COMPLETED'>('ALL');
    const [fleetCars, setFleetCars] = useState<any[]>([]);

    // Load fleet cars for mapping addresses
    useEffect(() => {
        const fetchCars = async () => {
            try {
                const { data } = await supabase.from('fleet_cars').select('placa, endereco');
                if (data) setFleetCars(data);
            } catch (err) {
                console.error("Error loading fleet cars for agent view:", err);
            }
        };
        fetchCars();
    }, []);

    // Load agents on mount
    useEffect(() => {
        const load = async () => {
            const { data } = await supabase.from('garage_agents').select('*').eq('is_active', true);
            if (data) setAgents(data);
            setLoading(false);
        };
        load();
    }, []);

    // Load today's checkins
    const fetchCheckins = useCallback(async () => {
        if (!currentAgent) return;
        try {
            const { data, error } = await supabase.functions.invoke('garage-agent-action', {
                body: { action: 'get_today', agent_id: currentAgent.id, agent_pin: currentAgent.pin }
            });
            if (error) throw error;
            if (data?.checkins) setCheckins(data.checkins);
        } catch (err) {
            console.error('Error fetching checkins:', err);
        }
    }, [currentAgent]);

    useEffect(() => { fetchCheckins(); }, [fetchCheckins]);

    // Realtime subscription
    useEffect(() => {
        if (!currentAgent) return;
        const today = new Date().toISOString().split('T')[0];
        const channel = supabase
            .channel('garage-agent-live')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'garage_checkins', filter: `shift_date=eq.${today}` },
                () => { fetchCheckins(); }
            ).subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [currentAgent, fetchCheckins]);

    // Agent action handler
    const handleAction = async (action: string, checkinId: string, data?: any) => {
        if (!currentAgent) return;
        setActionLoading(checkinId);
        try {
            const { data: result, error } = await supabase.functions.invoke('garage-agent-action', {
                body: { action, agent_id: currentAgent.id, agent_pin: currentAgent.pin, checkin_id: checkinId, data }
            });
            if (error) throw error;
            await fetchCheckins();
        } catch (err: any) {
            alert('Erro: ' + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    // --- Login Screen ---
    if (loading) return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
    );

    if (!currentAgent) return <AgentLogin agents={agents} onLogin={setCurrentAgent} />;

    // --- Stats ---
    const total = checkins.length;
    const waiting = checkins.filter(c => c.status === 'WAITING').length;
    const ready = checkins.filter(c => c.status === 'CAR_READY').length;
    const driverOk = checkins.filter(c => c.status === 'DRIVER_OK').length;
    const completed = checkins.filter(c => c.status === 'COMPLETED').length;
    const redCount = checkins.filter(c => calculateAuditColor(c) === 'RED').length;

    const filtered = filter === 'ALL' ? checkins : checkins.filter(c => c.status === filter);
    const today = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    // --- Kanban Columns ---
    const kanbanCols = [
        { key: 'WAITING', label: '⏳ Aguardando', items: filtered.filter(c => c.status === 'WAITING'), color: 'border-slate-600' },
        { key: 'CAR_READY', label: '🚗 Carro Pronto', items: filtered.filter(c => c.status === 'CAR_READY' || c.status === 'DRIVER_OK'), color: 'border-amber-500/50' },
        { key: 'COMPLETED', label: '✅ Validados', items: filtered.filter(c => c.status === 'COMPLETED'), color: 'border-emerald-500/50' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-lg border-b border-slate-800 px-4 py-3">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-white font-bold text-lg">Garagem Trianon MASP</h1>
                                <p className="text-slate-500 text-xs capitalize">{today}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 text-xs hidden sm:block">Agente: <strong className="text-slate-300">{currentAgent.name}</strong></span>
                            <button onClick={fetchCheckins} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors" title="Atualizar">
                                <RefreshCw className="w-4 h-4" />
                            </button>
                            <button onClick={() => setCurrentAgent(null)} className="p-2 bg-slate-800 hover:bg-red-900/50 rounded-lg text-slate-400 hover:text-red-400 transition-colors" title="Sair">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Stats bar */}
                    <div className="flex items-center gap-3 overflow-x-auto pb-1 scrollbar-hide">
                        <div className="flex items-center gap-1.5 bg-slate-800 rounded-lg px-3 py-1.5 text-xs whitespace-nowrap">
                            <span className="text-slate-500">Total:</span>
                            <span className="text-white font-bold">{total}</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-slate-800 rounded-lg px-3 py-1.5 text-xs whitespace-nowrap">
                            <span className="text-slate-500">⏳</span><span className="text-slate-300 font-bold">{waiting}</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-slate-800 rounded-lg px-3 py-1.5 text-xs whitespace-nowrap">
                            <span className="text-slate-500">🚗</span><span className="text-amber-400 font-bold">{ready + driverOk}</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-slate-800 rounded-lg px-3 py-1.5 text-xs whitespace-nowrap">
                            <span className="text-slate-500">✅</span><span className="text-emerald-400 font-bold">{completed}</span>
                        </div>
                        {redCount > 0 && (
                            <div className="flex items-center gap-1.5 bg-red-900/30 border border-red-500/30 rounded-lg px-3 py-1.5 text-xs whitespace-nowrap animate-pulse">
                                <span className="text-red-500">🔴</span><span className="text-red-400 font-bold">{redCount}</span>
                            </div>
                        )}

                        <div className="flex-1" />

                        {/* View toggles */}
                        <div className="flex bg-slate-800 rounded-lg overflow-hidden">
                            <button onClick={() => setViewMode('LIST')}
                                className={`p-2 transition-colors ${viewMode === 'LIST' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                                <List className="w-4 h-4" />
                            </button>
                            <button onClick={() => setViewMode('KANBAN')}
                                className={`p-2 transition-colors ${viewMode === 'KANBAN' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                                <LayoutGrid className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1 scrollbar-hide">
                        <Filter className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        {[
                            { key: 'ALL', label: 'Todos' },
                            { key: 'WAITING', label: '⏳ Aguardando' },
                            { key: 'CAR_READY', label: '🚗 Carro OK' },
                            { key: 'DRIVER_OK', label: '📲 Logando' },
                            { key: 'COMPLETED', label: '✅ Validados' },
                        ].map(f => (
                            <button key={f.key} onClick={() => setFilter(f.key as any)}
                                className={`px-3 py-1 rounded-full text-[11px] font-bold whitespace-nowrap transition-all ${
                                    filter === f.key ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-500 hover:text-slate-300'
                                }`}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-7xl mx-auto p-4">
                {filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-slate-600">
                        <Car className="w-12 h-12 mb-3" />
                        <p className="font-bold">Nenhum turno {filter !== 'ALL' ? 'nesse filtro' : 'registrado para hoje'}</p>
                        <p className="text-xs mt-1">Os turnos aparecem quando os motoristas registram no app.</p>
                    </div>
                ) : viewMode === 'LIST' ? (
                    <div className="space-y-3">
                        {filtered.map(c => (
                            <CheckinCard key={c.id} checkin={c} agent={currentAgent} onAction={handleAction} loading={actionLoading} fleetCars={fleetCars} />
                        ))}
                    </div>
                ) : (
                    /* Kanban view */
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {kanbanCols.map(col => (
                            <div key={col.key} className={`border-t-2 ${col.color} rounded-xl bg-slate-900/50 p-3`}>
                                <h3 className="text-sm font-bold text-slate-300 mb-3 flex items-center justify-between">
                                    {col.label}
                                    <span className="bg-slate-800 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded-full">{col.items.length}</span>
                                </h3>
                                <div className="space-y-2">
                                    {col.items.map(c => (
                                        <CheckinCard key={c.id} checkin={c} agent={currentAgent} onAction={handleAction} loading={actionLoading} fleetCars={fleetCars} />
                                    ))}
                                    {col.items.length === 0 && (
                                        <div className="text-center py-8 text-slate-700 text-xs">Vazio</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};
